import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import CircularProgress from '@mui/material/CircularProgress';
import { Breakpoint, Divider, IconButton } from '@mui/material';
import useStyles from './styles';
import analytics from '../../utils/analytics';
import bancameLogo from /* webpackPreload: true */ '../../assets/bancame_logo_dark.svg';

type AlertDialogProps = {
  buttonLabel?: string;
  dialogTitle?: string | React.ReactNode;
  dialogContent: string | React.ReactNode;
  action?: () => Promise<void> | void;
  onCloseAction?: () => void;
  button?: React.ReactElement | null;
  buttonVariant?: 'outlined' | 'text' | 'contained' | undefined;
  isTriggered?: boolean;
  triggerClose?: boolean;
  hideCloseButton?: boolean | 'corner';
  closeButtonDisabled?: boolean;
  closeButtonLabel?: string | React.ReactNode;
  submitButtonLabel?: string;
  submitButtonDisabled?: boolean;
  centerButtons?: boolean;
  closeAfterSubmit?: boolean;
  hideSubmitButton?: boolean;
  maxWidth?: Breakpoint;
  alternativeBackground?: boolean;
  buttonMaxWidth?: number | string;
  disabled?: boolean;
  pageCall?: {
    name: string;
    category: string;
    props?: Record<string, string | undefined>;
  };
  keepMounted?: boolean;
  hideLogo?: boolean;
  hideActions?: boolean;
  dialogContentStyle?: React.CSSProperties;
  submitButtonMaxWidth?: number | string;
  legend?: string | React.ReactNode;
  customLogo?: string;
  styles?: {
    primaryColor: string;
    secondaryColor: string;
  };
};
const defaultProps = {
  button: null,
  buttonLabel: '',
  buttonVariant: 'text',
  dialogTitle: '',
  hideCloseButton: false,
  closeButtonDisabled: false,
  closeButtonLabel: 'Cerrar',
  submitButtonLabel: 'Continuar',
  submitButtonDisabled: false,
  centerButtons: false,
  isTriggered: false,
  triggerClose: false,
  closeAfterSubmit: false,
  onCloseAction: () => {},
  action: () => {},
  hideSubmitButton: false,
  maxWidth: 'sm',
  alternativeBackground: false,
  buttonMaxWidth: 220,
  disabled: false,
  pageCall: undefined,
  keepMounted: false,
  hideLogo: false,
  hideActions: false,
  dialogContentStyle: {},
  submitButtonMaxWidth: 150,
  legend: null,
  customLogo: undefined,
  styles: undefined,
};
export default function AlertDialog(props: AlertDialogProps) {
  const {
    buttonLabel,
    dialogTitle,
    dialogContent,
    button,
    buttonVariant,
    isTriggered,
    triggerClose,
    hideCloseButton,
    closeButtonDisabled,
    closeButtonLabel,
    submitButtonLabel,
    submitButtonDisabled,
    centerButtons,
    closeAfterSubmit,
    onCloseAction = defaultProps.onCloseAction,
    action = defaultProps.action,
    hideSubmitButton,
    maxWidth,
    alternativeBackground,
    buttonMaxWidth,
    disabled,
    pageCall,
    keepMounted,
    hideLogo,
    hideActions,
    dialogContentStyle,
    submitButtonMaxWidth,
    legend,
    customLogo,
    styles,
  } = props;
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const handleClickOpen = () => {
    if (pageCall) {
      analytics.page(pageCall.category, pageCall.name, pageCall.props);
    }
    if (button) {
      button.props.onClick?.();
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    analytics.track('Cerrar Dialog', {
      initiator: 'usuario',
    });
    onCloseAction();
  };
  React.useEffect(() => {
    if (isTriggered) {
      handleClickOpen();
    }
  }, [isTriggered]);
  React.useEffect(() => {
    if (triggerClose) {
      setOpen(false);
    }
  }, [triggerClose]);

  const onSubmit = async () => {
    setLoading(true);
    await action();
    setLoading(false);
    if (closeAfterSubmit) {
      setOpen(false);
    }
  };

  return (
    <div>
      {button ? (
        React.cloneElement(button, {
          onClick: !disabled ? handleClickOpen : undefined,
        })
      ) : (
        <Button
          variant={buttonVariant}
          style={{ maxWidth: buttonMaxWidth, display: !buttonLabel ? 'none' : 'block' }}
          onClick={handleClickOpen}
          disabled={disabled}
        >
          {buttonLabel}
        </Button>
      )}
      <Dialog
        className={classes.Dialog}
        maxWidth={maxWidth}
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        keepMounted={keepMounted}
      >
        <div
          style={{
            marginBottom: hideLogo ? 4 : 2,
            textAlign: 'center',
            marginTop: 5,
            paddingInline: 20,
            display: hideCloseButton === 'corner' ? 'flex' : 'block',
            justifyContent:
              hideCloseButton === 'corner' ? 'space-between' : undefined,
            alignItems: hideCloseButton === 'corner' ? 'center' : undefined,
          }}
        >
          {hideCloseButton === 'corner' && <div />}
          {!hideLogo && (
            <div style={{ marginBottom: 8, textAlign: 'center', marginTop: 16 }}>
              <object data={bancameLogo} height="30px" aria-labelledby="banca.me logo" />
            </div>
          )}
          {customLogo && (
          <div style={{ marginBottom: 8, textAlign: 'center', marginTop: 16 }}>
            <object data={customLogo} height="30px" aria-labelledby="banca.me logo" />
          </div>
          )}
          {hideCloseButton === 'corner' && (
            <IconButton
              style={{ padding: 0, fontSize: 20 }}
              onClick={handleClose}
            >
              X
            </IconButton>
          )}
        </div>
        {!hideLogo && <Divider />}

        {(dialogTitle && dialogTitle !== '') && (
          <DialogTitle style={{ padding: 1, marginTop: hideLogo ? 15 : 10 }} id="alert-dialog-title">{dialogTitle}</DialogTitle>
        )}
        <DialogContent
          className={alternativeBackground ? classes.DialogContentAlt : undefined}
          style={dialogContentStyle}
        >
          <DialogContentText id="alert-dialog-description" component="div">
            {dialogContent}
          </DialogContentText>
        </DialogContent>
        {!hideActions && (
          <DialogActions style={{ margin: centerButtons ? 'auto' : undefined }}>
              {legend && (
              <div style={{
                textAlign: 'left', width: '100%', paddingInline: 2, display: 'flex', alignItems: 'center',
              }}
              >
                {legend}
              </div>
              )}
              {!hideCloseButton && (
                <Button
                  disabled={loading || closeButtonDisabled}
                  onClick={handleClose}
                  style={{ textTransform: 'none', minWidth: 'fit-content' }}
                >
                  {closeButtonLabel}
                </Button>
              )}
              {!hideSubmitButton && (
                <Button
                  disabled={loading || submitButtonDisabled}
                  variant="contained"
                  color="primary"
                  onClick={onSubmit}
                  style={{
                    maxWidth: submitButtonMaxWidth,
                    backgroundColor: styles?.secondaryColor,
                    color: styles?.primaryColor,
                  }}
                >
                  {loading ? <CircularProgress color="secondary" size={15} /> : <span>{submitButtonLabel}</span>}
                </Button>
              )}
          </DialogActions>
        )}
      </Dialog>
    </div>
  );
}

AlertDialog.defaultProps = defaultProps;
